import { Box, Icon, IconVariant } from '@rbilabs/universal-components';
import { CardTypes, IPaymentMethod } from 'features/payment/types';
import { ResponsiveValue } from 'native-base/lib/typescript/components/types';
import { ISizes } from 'native-base/lib/typescript/theme/base/sizes';

import { primitive } from 'styles/constants';

const getCreditCardType = (paymentMethod: IPaymentMethod | undefined) => {
  const creditCardType = paymentMethod?.credit?.cardType.toLocaleUpperCase();
  if (creditCardType === 'MC') {
    return CardTypes.MASTERCARD;
  }

  return creditCardType as CardTypes;
};

const getIconProps = (paymentMethod: IPaymentMethod | undefined, bordered: boolean) => {
  const iconPropsMap: { title: string; variant: IconVariant; matches: boolean }[] = [
    {
      title: 'Visa',
      variant: bordered ? 'visa' : 'visaNoBorder',
      matches: getCreditCardType(paymentMethod) === CardTypes.VISA,
    },
    {
      title: 'Mastercard',
      variant: bordered ? 'mastercard' : 'mastercardNoBorder',
      matches: getCreditCardType(paymentMethod) === CardTypes.MASTERCARD,
    },
    {
      title: 'American Express',
      variant: bordered ? 'amex' : 'amexNoBorder',
      matches: getCreditCardType(paymentMethod) === CardTypes.AMEX,
    },
    {
      title: 'Discover',
      variant: bordered ? 'discover' : 'discoverNoBorder',
      matches: getCreditCardType(paymentMethod) === CardTypes.DISCOVER,
    },
    {
      title: 'JCB',
      variant: bordered ? 'jcb' : 'jcbNoBorder',
      matches: getCreditCardType(paymentMethod) === CardTypes.JCB,
    },
    {
      title: 'Diners',
      variant: bordered ? 'diners' : 'dinersNoBorder',
      matches: getCreditCardType(paymentMethod) === CardTypes.DINERS,
    },
    {
      title: 'Google Pay',
      variant: 'googlePayBrand',
      matches: getCreditCardType(paymentMethod) === CardTypes.GOOGLE_PAY,
    },
    {
      title: 'Apple Pay',
      variant: bordered ? 'applepay' : 'applepayNoBorder',
      matches: getCreditCardType(paymentMethod) === CardTypes.APPLE_PAY,
    },
    {
      title: 'Paypal',
      variant: bordered ? 'paypal' : 'paypalNoBorder',
      matches: !!paymentMethod?.paypal,
    },
    {
      title: 'Venmo',
      variant: bordered ? 'venmo' : 'venmoNoBorder',
      matches: !!paymentMethod?.venmo,
    },
    {
      title: 'Cash',
      variant: bordered ? 'cash' : 'cashNoBorder',
      matches: !!paymentMethod?.cash,
    },
    {
      title: 'Cash App',
      variant: bordered ? 'cashApp' : 'cashAppNoBorder',
      matches: !!paymentMethod?.cashapp,
    },
    {
      title: 'Gift Card',
      variant: bordered ? 'giftCard' : 'giftCardNoBorder',
      matches: !!paymentMethod?.prepaid,
    },
  ];

  return iconPropsMap.find(iconProps => iconProps.matches);
};

const BlankCard = Box.withConfig({
  backgroundColor: primitive.$greyFour,
  height: '$5',
  width: '$6',
});

type PaymentMethodIconProps = {
  paymentMethod: IPaymentMethod | undefined;
  size?: ResponsiveValue<ISizes | (string & {}) | number>;
  bordered?: boolean;
};
export function PaymentMethodIcon({
  paymentMethod,
  bordered = false,
  size = 'lg',
}: PaymentMethodIconProps) {
  const matchedIcon = getIconProps(paymentMethod, bordered);
  if (!matchedIcon) {
    return <BlankCard />;
  }

  return (
    <Icon accessibilityHidden title={matchedIcon.title} variant={matchedIcon.variant} size={size} />
  );
}
